import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Eye, X, Trash2, CheckCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const SelectedQuestionsModal = ({
  isOpen,
  onClose,
  selectedQuestions = [],
  onRemoveQuestion,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const questionsPerPage = 5;

  const totalPages = Math.ceil(selectedQuestions.length / questionsPerPage);
  const startIndex = (currentPage - 1) * questionsPerPage;
  const endIndex = startIndex + questionsPerPage;
  const currentQuestions = selectedQuestions.slice(startIndex, endIndex);

  const renderQuestionContent = (question) => {
    const questionType = question.qtyp?.toLowerCase();

    switch (questionType) {
      case "single_choice":
      case "multiple_choice":
        return (
          <div className="space-y-3">
            <p className="font-medium text-base">{question.question}</p>
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Options & Answers:
              </h4>
              {question.choices?.map((choice, index) => {
                const isCorrect = question.answers?.includes(choice);
                return (
                  <div
                    key={index}
                    className={`p-3 rounded-lg text-sm border ${
                      isCorrect
                        ? "bg-green-50 text-green-800 border-green-200"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {isCorrect && (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      )}
                      <span className="font-medium">
                        {String.fromCharCode(65 + index)}.
                      </span>
                      <span>{choice}</span>
                      {isCorrect && (
                        <Badge
                          variant="secondary"
                          className="ml-auto bg-green-100 text-green-700"
                        >
                          Correct Answer
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case "true_false":
        return (
          <div className="space-y-3">
            <p className="font-medium text-base">{question.question}</p>
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Options & Answer:
              </h4>
              <div
                className={`p-3 rounded-lg text-sm border flex items-center gap-2 ${
                  question.answers?.includes("True") ||
                  question.answers?.includes("true")
                    ? "bg-green-50 text-green-800 border-green-200"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                {(question.answers?.includes("True") ||
                  question.answers?.includes("true")) && (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                )}
                <span className="font-medium">True</span>
                {(question.answers?.includes("True") ||
                  question.answers?.includes("true")) && (
                  <Badge
                    variant="secondary"
                    className="ml-auto bg-green-100 text-green-700"
                  >
                    Correct Answer
                  </Badge>
                )}
              </div>
              <div
                className={`p-3 rounded-lg text-sm border flex items-center gap-2 ${
                  question.answers?.includes("False") ||
                  question.answers?.includes("false")
                    ? "bg-green-50 text-green-800 border-green-200"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                {(question.answers?.includes("False") ||
                  question.answers?.includes("false")) && (
                  <CheckCircle className="h-4 w-4 text-green-600" />
                )}
                <span className="font-medium">False</span>
                {(question.answers?.includes("False") ||
                  question.answers?.includes("false")) && (
                  <Badge
                    variant="secondary"
                    className="ml-auto bg-green-100 text-green-700"
                  >
                    Correct Answer
                  </Badge>
                )}
              </div>
            </div>
          </div>
        );

      case "short_answer":
      case "fill_in_blank":
        return (
          <div className="space-y-3">
            <p className="font-medium text-base">{question.question}</p>
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-700">
                Expected Answer(s):
              </h4>
              <div className="p-3 bg-green-50 text-green-800 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="font-medium">
                    {question.answers?.length > 0
                      ? question.answers.join(", ")
                      : "No answer provided"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );

      case "essay":
      case "descriptive":
        return (
          <div className="space-y-3">
            <p className="font-medium text-base">{question.question}</p>
            <div className="space-y-2">
              {question.answers?.length > 0 ? (
                <>
                  <h4 className="text-sm font-semibold text-gray-700">
                    Sample Answer:
                  </h4>
                  <div className="p-3 bg-green-50 text-green-800 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                      <span>{question.answers.join(", ")}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-3 bg-blue-50 text-blue-800 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4 text-blue-600" />
                    <span className="font-medium">Note:</span>
                    <span>Essay questions require manual evaluation</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-3">
            <p className="font-medium text-base">{question.question}</p>
            {question.choices && question.choices.length > 0 ? (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Options & Answers:
                </h4>
                {question.choices.map((choice, index) => {
                  const isCorrect = question.answers?.includes(choice);
                  return (
                    <div
                      key={index}
                      className={`p-3 rounded-lg text-sm border ${
                        isCorrect
                          ? "bg-green-50 text-green-800 border-green-200"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {isCorrect && (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        )}
                        <span className="font-medium">
                          {String.fromCharCode(65 + index)}.
                        </span>
                        <span>{choice}</span>
                        {isCorrect && (
                          <Badge
                            variant="secondary"
                            className="ml-auto bg-green-100 text-green-700"
                          >
                            Correct Answer
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700">
                  Answer(s):
                </h4>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="font-medium text-green-800">
                      {question.answers?.length > 0
                        ? question.answers.join(", ")
                        : "No answer provided"}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
    }
  };

  // ... keep existing code (getQuestionTypeBadge, handleRemoveQuestion functions)
  const getQuestionTypeBadge = (qtyp) => {
    const typeMap = {
      SINGLE_CHOICE: "Single Choice",
      MULTIPLE_CHOICE: "Multiple Choice",
      TRUE_FALSE: "True/False",
      SHORT_ANSWER: "Short Answer",
      ESSAY: "Essay",
      FILL_IN_BLANK: "Fill in Blank",
    };
    return typeMap[qtyp] || qtyp?.replace("_", " ") || "Unknown";
  };

  const handleRemoveQuestion = (questionId) => {
    if (onRemoveQuestion) {
      onRemoveQuestion(questionId);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Selected Questions ({selectedQuestions.length})
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto pr-2">
          <div className="space-y-4">
            {selectedQuestions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No questions selected yet
              </div>
            ) : (
              <>
                <Accordion type="single" collapsible className="space-y-4">
                  {currentQuestions.map((question, index) => (
                    <AccordionItem
                      key={question._id}
                      value={`question-${question._id}`}
                      className="border rounded-lg"
                    >
                      <Card className="border-0">
                        <AccordionTrigger className="hover:no-underline px-6 py-4">
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2 flex-wrap text-left">
                              <span className="font-semibold">
                                Question {startIndex + index + 1}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {question.category}
                              </Badge>
                              {question.subCategory && (
                                <Badge variant="outline" className="text-xs">
                                  {question.subCategory}
                                </Badge>
                              )}
                              <Badge
                                variant="outline"
                                className={`text-xs capitalize ${
                                  question.difficultyLevel === "easy"
                                    ? "bg-green-100 text-green-800"
                                    : question.difficultyLevel === "medium"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {question.difficultyLevel}
                              </Badge>
                              <Badge variant="secondary" className="text-xs">
                                {getQuestionTypeBadge(question.qtyp)}
                              </Badge>
                              {question.tags && question.tags.length > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  {question.tags[0]}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">
                                {question.value} points
                              </span>
                              {onRemoveQuestion && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveQuestion(question._id);
                                  }}
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-6 pb-4">
                          {renderQuestionContent(question)}
                        </AccordionContent>
                      </Card>
                    </AccordionItem>
                  ))}
                </Accordion>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() =>
                            setCurrentPage((p) => Math.max(p - 1, 1))
                          }
                          disabled={currentPage === 1}
                          className={
                            currentPage === 1
                              ? "pointer-events-none opacity-50"
                              : "cursor-pointer"
                          }
                        />
                      </PaginationItem>

                      {(() => {
                        const pages = [];
                        const maxVisible = 5;
                        let start = Math.max(
                          1,
                          currentPage - Math.floor(maxVisible / 2)
                        );
                        let end = start + maxVisible - 1;

                        if (end > totalPages) {
                          end = totalPages;
                          start = Math.max(1, end - maxVisible + 1);
                        }

                        for (let i = start; i <= end; i++) {
                          pages.push(
                            <PaginationItem key={i}>
                              <PaginationLink
                                isActive={currentPage === i}
                                onClick={() => setCurrentPage(i)}
                                className="cursor-pointer"
                              >
                                {i}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        }

                        return pages;
                      })()}

                      <PaginationItem>
                        <PaginationNext
                          onClick={() =>
                            setCurrentPage((p) => Math.min(p + 1, totalPages))
                          }
                          disabled={currentPage === totalPages}
                          className={
                            currentPage === totalPages
                              ? "pointer-events-none opacity-50"
                              : "cursor-pointer"
                          }
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                )}
              </>
            )}
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex justify-end pt-4 border-t flex-shrink-0">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelectedQuestionsModal;
